import { create } from 'zustand';
import type {
    BoardColumn,
    BoardCardData,
    BoardColumnState,
    BoardSortState,
    BoardFilters,
    FetchFunction,
    FetchParams,
    PendingMove,
    ViewMode,
} from '../types/board.types';

const ITEMS_PER_PAGE = 15;

function emptyColumnState<T extends BoardCardData>(): BoardColumnState<T> {
    return {
        items: [],
        total: 0,
        page: 0,
        hasMore: true,
        isLoading: false,
        isLoadingMore: false,
        error: null,
        sort: null,
        filters: {},
    };
}

function reconcileItems<T extends BoardCardData>(
    columnId: string,
    serverItems: T[],
    serverTotal: number,
    pendingMoves: PendingMove<T>[]
): { items: T[]; total: number } {
    let items = [...serverItems];
    let total = serverTotal;

    for (const move of pendingMoves) {
        const isFrom = move.fromColumnId === columnId;
        const isTo = move.toColumnId === columnId;

        if (isFrom && isTo) {
            if (move.fieldOverrides) {
                const idx = items.findIndex((i) => i._id === move.cardId);
                if (idx !== -1) {
                    items[idx] = { ...items[idx], ...move.fieldOverrides } as T;
                }
            }
        } else if (isFrom && !isTo) {
            const idx = items.findIndex((i) => i._id === move.cardId);
            if (idx !== -1) {
                items.splice(idx, 1);
                total--;
            }
        } else if (isTo && !isFrom) {
            if (!items.some((i) => i._id === move.cardId)) {
                const card = move.fieldOverrides
                    ? ({ ...move.card, ...move.fieldOverrides } as T)
                    : move.card;
                items.unshift(card);
                total++;
            } else if (move.fieldOverrides) {
                const idx = items.findIndex((i) => i._id === move.cardId);
                if (idx !== -1) {
                    items[idx] = { ...items[idx], ...move.fieldOverrides } as T;
                }
            }
        }
    }

    return { items, total };
}

let _opCounter = 0;
function nextOpId(): string {
    return `op_${Date.now()}_${++_opCounter}`;
}

export interface BoardState<T extends BoardCardData = BoardCardData> {
    columns: BoardColumn[];
    columnStates: Record<string, BoardColumnState<T>>;
    viewMode: ViewMode;
    searchTerm: string;
    refreshCounter: number;
    selectedIds: Set<string>;
    multiSelectMode: boolean;
    expandedColumns: Set<string>;

    pendingMoves: PendingMove<T>[];
    _loadSeq: Record<string, number>;

    setColumns: (columns: BoardColumn[]) => void;
    setViewMode: (mode: ViewMode) => void;
    setSearchTerm: (term: string) => void;
    setColumnSort: (columnId: string, sort: BoardSortState | null) => void;
    setColumnFilters: (columnId: string, filters: BoardFilters) => void;
    refresh: () => void;
    toggleSelect: (id: string) => void;
    clearSelection: () => void;
    setMultiSelectMode: (on: boolean) => void;
    toggleExpandColumn: (columnId: string) => void;

    loadColumn: (columnId: string, fetchFn: FetchFunction<T>) => Promise<void>;
    loadMoreColumn: (columnId: string, fetchFn: FetchFunction<T>) => Promise<void>;

    moveCardOptimistic: (
        cardId: string,
        fromColumnId: string,
        toColumnId: string,
        toIndex: number
    ) => void;

    updateCardField: (
        cardId: string,
        columnId: string,
        field: string,
        value: unknown
    ) => void;

    addPendingMove: (cardId: string, fromColumnId: string, toColumnId: string, fieldOverrides?: Record<string, unknown>) => string;
    removePendingMove: (operationId: string) => void;

    bulkMoveOptimistic: (
        cardIds: string[],
        fromColumnId: string,
        toColumnId: string
    ) => string[];
}

export function createBoardStore<T extends BoardCardData = BoardCardData>() {
    return create<BoardState<T>>((set, get) => ({
        columns: [],
        columnStates: {},
        viewMode: 'kanban',
        searchTerm: '',
        refreshCounter: 0,
        selectedIds: new Set(),
        multiSelectMode: false,
        expandedColumns: new Set(),

        pendingMoves: [],
        _loadSeq: {},

        setColumns: (columns) => {
            const existing = get().columnStates;
            const next: Record<string, BoardColumnState<T>> = {};
            for (const col of columns) {
                next[col.id] = existing[col.id] ?? emptyColumnState<T>();
            }
            set({ columns, columnStates: next });
        },

        setViewMode: (mode) => set({ viewMode: mode }),

        setSearchTerm: (term) => set({ searchTerm: term }),

        setColumnSort: (columnId, sort) => {
            const states = { ...get().columnStates };
            if (states[columnId]) {
                states[columnId] = { ...states[columnId], sort, items: [], page: 0, hasMore: true };
            }
            set({ columnStates: states });
        },

        setColumnFilters: (columnId, filters) => {
            const states = { ...get().columnStates };
            if (states[columnId]) {
                states[columnId] = { ...states[columnId], filters, items: [], page: 0, hasMore: true };
            }
            set({ columnStates: states });
        },

        refresh: () => set({ refreshCounter: get().refreshCounter + 1 }),

        toggleSelect: (id) => {
            const next = new Set(get().selectedIds);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            set({ selectedIds: next });
        },

        clearSelection: () => set({ selectedIds: new Set(), multiSelectMode: false }),

        setMultiSelectMode: (on) => {
            set({ multiSelectMode: on });
            if (!on) set({ selectedIds: new Set() });
        },

        toggleExpandColumn: (columnId) => {
            const next = new Set(get().expandedColumns);
            if (next.has(columnId)) next.delete(columnId);
            else next.add(columnId);
            set({ expandedColumns: next });
        },

        addPendingMove: (cardId, fromColumnId, toColumnId, fieldOverrides?) => {
            const opId = nextOpId();
            const state = get().columnStates[toColumnId];
            const card = state?.items.find((i) => i._id === cardId)
                ?? get().columnStates[fromColumnId]?.items.find((i) => i._id === cardId);
            if (!card) return opId;

            set({
                pendingMoves: [...get().pendingMoves, {
                    operationId: opId,
                    cardId,
                    card: { ...card } as T,
                    fromColumnId,
                    toColumnId,
                    fieldOverrides,
                }],
            });
            return opId;
        },

        removePendingMove: (operationId) => {
            set({
                pendingMoves: get().pendingMoves.filter((m) => m.operationId !== operationId),
            });
        },

        loadColumn: async (columnId, fetchFn) => {
            const { columnStates, columns, searchTerm, _loadSeq } = get();
            const col = columns.find((c) => c.id === columnId);
            if (!col) return;

            const seq = (_loadSeq[columnId] ?? 0) + 1;
            set({ _loadSeq: { ...get()._loadSeq, [columnId]: seq } });

            const state = columnStates[columnId] ?? emptyColumnState<T>();

            set({
                columnStates: {
                    ...get().columnStates,
                    [columnId]: { ...state, isLoading: true, error: null },
                },
            });

            try {
                const params: FetchParams = {
                    columnId,
                    status: col.apiStatusValue,
                    searchTerm,
                    page: 1,
                    limit: ITEMS_PER_PAGE,
                    sort: state.sort,
                    filters: state.filters,
                };
                const result = await fetchFn(params);

                if (get()._loadSeq[columnId] !== seq) return;

                const { items: reconciledItems, total: reconciledTotal } = reconcileItems(
                    columnId,
                    result.data as T[],
                    result.total,
                    get().pendingMoves
                );

                set({
                    columnStates: {
                        ...get().columnStates,
                        [columnId]: {
                            ...get().columnStates[columnId],
                            items: reconciledItems,
                            total: reconciledTotal,
                            page: 1,
                            hasMore: result.hasMore,
                            isLoading: false,
                            error: null,
                        },
                    },
                });
            } catch (err: unknown) {
                if (get()._loadSeq[columnId] !== seq) return;
                const msg = err instanceof Error ? err.message : 'Failed to load';
                set({
                    columnStates: {
                        ...get().columnStates,
                        [columnId]: {
                            ...get().columnStates[columnId],
                            isLoading: false,
                            error: msg,
                        },
                    },
                });
            }
        },

        loadMoreColumn: async (columnId, fetchFn) => {
            const { columnStates, columns, searchTerm } = get();
            const col = columns.find((c) => c.id === columnId);
            const state = columnStates[columnId];
            if (!col || !state || state.isLoadingMore || !state.hasMore) return;

            set({
                columnStates: {
                    ...get().columnStates,
                    [columnId]: { ...state, isLoadingMore: true },
                },
            });

            try {
                const nextPage = state.page + 1;
                const params: FetchParams = {
                    columnId,
                    status: col.apiStatusValue,
                    searchTerm,
                    page: nextPage,
                    limit: ITEMS_PER_PAGE,
                    sort: state.sort,
                    filters: state.filters,
                };
                const result = await fetchFn(params);

                set({
                    columnStates: {
                        ...get().columnStates,
                        [columnId]: {
                            ...get().columnStates[columnId],
                            items: [...get().columnStates[columnId].items, ...(result.data as T[])],
                            total: result.total,
                            page: nextPage,
                            hasMore: result.hasMore,
                            isLoadingMore: false,
                        },
                    },
                });
            } catch {
                set({
                    columnStates: {
                        ...get().columnStates,
                        [columnId]: {
                            ...get().columnStates[columnId],
                            isLoadingMore: false,
                        },
                    },
                });
            }
        },

        moveCardOptimistic: (cardId, fromColumnId, toColumnId, toIndex) => {
            const states = { ...get().columnStates };
            const fromState = states[fromColumnId];
            if (!fromState) return;

            const cardIndex = fromState.items.findIndex((i) => i._id === cardId);
            if (cardIndex === -1) return;
            const card = fromState.items[cardIndex];

            if (fromColumnId === toColumnId) {
                const newItems = [...fromState.items];
                newItems.splice(cardIndex, 1);
                const clampedIndex = Math.min(toIndex, newItems.length);
                newItems.splice(clampedIndex, 0, card);
                states[fromColumnId] = { ...fromState, items: newItems };
            } else {
                const toState = states[toColumnId];
                if (!toState) return;

                const newFromItems = [...fromState.items];
                newFromItems.splice(cardIndex, 1);

                const newToItems = [...toState.items];
                const clampedIndex = Math.min(toIndex, newToItems.length);
                newToItems.splice(clampedIndex, 0, card);

                states[fromColumnId] = { ...fromState, items: newFromItems, total: fromState.total - 1 };
                states[toColumnId] = { ...toState, items: newToItems, total: toState.total + 1 };
            }

            set({ columnStates: states });
        },

        updateCardField: (cardId, columnId, field, value) => {
            const states = { ...get().columnStates };
            const colState = states[columnId];
            if (!colState) return;

            const idx = colState.items.findIndex((i) => i._id === cardId);
            if (idx === -1) return;

            const updated = { ...colState.items[idx], [field]: value } as T;
            const newItems = [...colState.items];
            newItems[idx] = updated;
            states[columnId] = { ...colState, items: newItems };
            set({ columnStates: states });
        },

        bulkMoveOptimistic: (cardIds, fromColumnId, toColumnId) => {
            const opIds: string[] = [];
            for (const cardId of cardIds) {
                get().moveCardOptimistic(cardId, fromColumnId, toColumnId, 0);
                const opId = get().addPendingMove(cardId, fromColumnId, toColumnId);
                opIds.push(opId);
            }
            return opIds;
        },
    }));
}
